/**
 * Email validation utility for forms
 * Blocks personal email domains (gmail, yahoo, hotmail, etc.) to encourage business emails
 */

// Blocked email domain prefixes - blocks any domain starting with these strings.
// Kept to consumer mail providers only; ISP and telco domains are left alone because their
// staff use the same domain for work.
export const BLOCKED_EMAIL_DOMAIN_PREFIXES = [
  'email',
  'gmail',
  'googlemail',
  'yahoo',
  'ymail',
  'rocketmail',
  'hotmail',
  'outlook',
  'live',
  'msn',
  'aol',
  'icloud',
  'me',
  'mac',
  'proton',
  'protonmail',
  'pm',
  'gmx',
  'yandex',
  'zoho',
  'inbox',
  'fastmail',
  'hey',
  'hushmail',
  'tutanota',
  'tuta',
  'sbcglobal',
  'bellsouth',
  'earthlink',
  'optonline',
  'btinternet',
  'wanadoo',
  'libero',
  't-online',
  'rediffmail',
  'qq',
  '163',
  '126',
  'sina',
  'naver',
] as const

// Exact consumer domains whose prefix is also a real work domain (mail.mil, usa.gov)
export const BLOCKED_EMAIL_DOMAINS = ['mail.com', 'mail.ru', 'usa.com', 'usa.net'] as const

/**
 * Validates email format using standard business email pattern
 */
export const EMAIL_FORMAT_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

/**
 * Validates email domain against blocked prefixes
 * @param email - Email address to validate
 * @returns true if valid, error message if blocked
 */
export const validateEmailDomain = (email: string): string | true => {
  if (!email || !email.includes('@')) {
    return true
  }

  const emailDomain = email.toLowerCase().split('@')[1]

  const blockedPrefix = BLOCKED_EMAIL_DOMAIN_PREFIXES.find((prefix) => {
    return emailDomain === prefix || emailDomain.startsWith(prefix + '.')
  })

  if (blockedPrefix || (BLOCKED_EMAIL_DOMAINS as readonly string[]).includes(emailDomain)) {
    return 'Please use your work email address.'
  }

  return true
}

/**
 * Complete email validation rules for react-hook-form
 */
export const EMAIL_VALIDATION_RULES = {
  required: 'Required',
  pattern: {
    value: EMAIL_FORMAT_PATTERN,
    message: 'Email must be formatted correctly.',
  },
  validate: validateEmailDomain,
} as const
